import { APProgressLoading } from '@ap/design-system';
import { ApolloProvider } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { AuthenticationResult, EventType } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { StatusCodes } from 'http-status-codes';
import { JSX, useContext, useEffect, useState } from 'react';

import getApolloClient from '../config/apollo/getApolloClient';
import useGetMsalAccessToken from '../features/users/services/useGetMsalAccessToken';
import useLogEvent, { LogLevel } from '../helper/useLogEvent';
import useNestClient from '../helper/useNestClient';
import usePrevious from '../helper/usePrevious';
import User from '../models/User';
import { AppContext } from './App';

const containerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  flexDirection: 'column',
  gap: '16px',
  height: '100%',
  padding: '32px',
};

interface Response extends User {}

interface Props {
  children: JSX.Element;
}

function AppHasuraProvider(props: Props) {
  const { instance } = useMsal();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const prevAccessToken = usePrevious(accessToken);
  const { getAccessToken } = useGetMsalAccessToken();

  const { user, setUser, apolloClient, setApolloClient } = useContext(AppContext);
  const { logEvent } = useLogEvent();
  const { nestClient } = useNestClient();

  useEffect(() => {
    instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && accessToken !== prevAccessToken)
      ) {
        const response = event.payload as AuthenticationResult;

        if (response) {
          setAccessToken(response.accessToken);
        }
      }
    });
  }, []);

  const withToken = setContext(async (_, { headers }) => {
    const token = await getAccessToken();

    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : null,
      },
    };
  });

  useEffect(() => {
    const initializeAuthenticatedUser = async () => {
      if (!accessToken) {
        return;
      }

      try {
        // Call the API to authenticate / get user data
        const response = await nestClient.get<Response>('users/me');

        if (response.status !== StatusCodes.OK) {
          throw new Error('Unable to get authenticated user data');
        }

        setUser(response.data as User);

        const localApolloClient = await getApolloClient(withToken);
        setApolloClient(localApolloClient);
      } catch (e) {
        logEvent(LogLevel.Info, 'Failed to get authenticated user data');
        console.error(e);
      }
    };

    if (accessToken && (!user || !apolloClient) && accessToken != prevAccessToken) {
      initializeAuthenticatedUser();
    }
  }, [accessToken, user]);

  if (!user || !apolloClient) {
    return (
      <Container maxWidth='sm' sx={{ flex: 1 }}>
        <Box sx={containerStyle}>
          <APProgressLoading>Initiate database connection...</APProgressLoading>
        </Box>
      </Container>
    );
  }

  return <ApolloProvider client={apolloClient}>{props.children}</ApolloProvider>;
}

export default AppHasuraProvider;
